// Player, pellet, bouncer rendering — glass-and-obsidian pass (GDD §14.5).
//
// Pellets and bouncers are cached shapes, repositioned per frame. Players are
// redrawn each frame (health ring arc + directional glow + invuln pulse) —
// ≤ 20 dots, trivially cheap.

import { Graphics, Text, type Container } from 'pixi.js';
import type { Bouncer, HexGrid, PlayerId } from '@evox/shared';
import { tuning } from '@evox/shared';
import type { View } from '../net/connection.js';
import { edgeParamToPx, nearestCopy, type Px } from './geometry.js';
import { theme } from './theme.js';

const pelletSprites = new Map<string, Graphics>();
const playerSprites = new Map<string, Graphics>();
const nameLabels = new Map<string, Text>();
let bouncerLayerBuilt = false;

function hexColor(s: string): number {
  return parseInt(s, 16) || 0xffffff;
}

/** Static bouncers: matte squares — the only opaque thing in the world. */
export function drawBouncers(layer: Container, grid: HexGrid, bouncers: Bouncer[], camera: Px): void {
  if (!bouncerLayerBuilt) {
    bouncerLayerBuilt = true;
    for (const b of bouncers) {
      const g = new Graphics();
      g.rect(-8, -8, 16, 16)
        .fill({ color: theme.bouncer })
        .stroke({ width: 1.5, color: theme.bouncerEdge, alpha: 0.9 });
      g.label = b.id;
      layer.addChild(g);
    }
  }
  let i = 0;
  for (const b of bouncers) {
    const g = layer.children[i++] as Graphics;
    const pos = nearestCopy(edgeParamToPx(grid, b.edgeId, b.t), camera, grid.dims);
    g.position.set(pos.x, pos.y);
  }
}

export function drawEntities(
  layer: Container,
  grid: HexGrid,
  view: View,
  localPlayerId: PlayerId,
  camera: Px,
  nowMs: number,
): void {
  const alive = new Set<string>();

  // Pellets — small jewels lit from within.
  for (const p of view.pellets) {
    alive.add(p.id);
    let g = pelletSprites.get(p.id);
    if (!g) {
      g = new Graphics();
      g.circle(0, 0, 9).fill({ color: theme.pelletHalo, alpha: 0.16 });
      g.circle(0, 0, 4.5).fill({ color: theme.pellet, alpha: 0.95 });
      pelletSprites.set(p.id, g);
      layer.addChild(g);
    }
    const pos = nearestCopy(p.px, camera, grid.dims);
    g.position.set(pos.x, pos.y);
  }

  // Players — translucent jewel dots with health ring + directional glow.
  for (const pl of view.players) {
    alive.add(pl.id);
    let g = playerSprites.get(pl.id);
    if (!g) {
      g = new Graphics();
      playerSprites.set(pl.id, g);
      layer.addChild(g);
    }
    const pos = nearestCopy(pl.px, camera, grid.dims);
    g.position.set(pos.x, pos.y);
    g.clear();

    const col = hexColor(pl.color);
    const isLocal = pl.id === localPlayerId;
    const r = isLocal ? 8.5 : 7.5; // slightly smaller dots (2026-06-12)

    // Directional glow: a soft leading-edge blob.
    g.circle(pl.heading.x * r * 0.9, pl.heading.y * r * 0.9, r * 0.75).fill({
      color: col,
      alpha: 0.30,
    });
    // Body: tinted glass.
    g.circle(0, 0, r).fill({ color: col, alpha: 0.88 });
    g.circle(0, 0, r).stroke({ width: 1, color: 0xffffff, alpha: 0.25 });

    // Health ring: same hue, brighter, thins as health drops (counterclockwise).
    const frac = Math.max(0, Math.min(1, pl.health / tuning.maxHealth));
    if (frac > 0.01) {
      const start = -Math.PI / 2;
      g.arc(0, 0, r + 4, start, start + frac * Math.PI * 2).stroke({
        width: 2.5,
        color: col,
        alpha: 0.95,
      });
    }

    // Respawn invulnerability: pulsing white ring.
    if (pl.invuln) {
      const pulse = 0.5 + 0.5 * Math.sin(nowMs / 110);
      g.circle(0, 0, r + 8).stroke({ width: 2, color: 0xffffff, alpha: 0.25 + 0.45 * pulse });
    }

    // Name label above the dot.
    let label = nameLabels.get(pl.id);
    if (!label) {
      label = new Text({
        text: pl.name,
        style: { fontFamily: 'system-ui, sans-serif', fontSize: 12, fill: 0xc8d2e8 },
      });
      label.anchor.set(0.5, 1);
      label.alpha = 0.75;
      nameLabels.set(pl.id, label);
      layer.addChild(label);
    }
    label.position.set(pos.x, pos.y - (r + 10));
  }

  // Sweep stale sprites (eaten pellets, departed players).
  for (const [id, g] of pelletSprites) {
    if (!alive.has(id)) {
      g.destroy();
      pelletSprites.delete(id);
    }
  }
  for (const [id, g] of playerSprites) {
    if (!alive.has(id)) {
      g.destroy();
      playerSprites.delete(id);
      const label = nameLabels.get(id);
      if (label) {
        label.destroy();
        nameLabels.delete(id);
      }
    }
  }
}
